import { ApplicationRef, ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MainDataStore } from '@app/store/main-data.store';

import { RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { ButtonDirective } from 'primeng/button';
import { concat, first, interval } from 'rxjs';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { NavbarComponent } from '@app/navbar/navbar.component';
import { ToastModule } from 'primeng/toast';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgOptimizedImage } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NavbarComponent,
    ConfirmDialogModule,
    RouterOutlet,
    ToastModule,
    FontAwesomeModule,
    NgOptimizedImage,
    ButtonDirective,
  ],
})
export class AppComponent {
  hasNewVersion = signal<boolean>(false);

  isAllowedToAccess = computed(() => {
    return this.mainDataStore.admin() || this.mainDataStore.me();
  });

  private swUpdate = inject(SwUpdate);
  private appRef = inject(ApplicationRef);
  private mainDataStore = inject(MainDataStore);

  constructor() {
    this.setupUpdateService();

    this.appRef.isStable.pipe(first((isStable) => isStable)).subscribe(() => {
      // eslint-disable-next-line no-console
      console.log('App is stable');
    });
  }

  private setupUpdateService(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((evt) => {
        switch (evt.type) {
          case 'VERSION_DETECTED':
            // eslint-disable-next-line no-console
            console.log(`Downloading new app version: ${evt.version.hash}`);
            break;
          case 'NO_NEW_VERSION_DETECTED':
            // eslint-disable-next-line no-console
            console.log(`No new app version available.`);
            break;
          case 'VERSION_READY':
            // eslint-disable-next-line no-console
            console.log(`Current app version: ${evt.currentVersion.hash}`);
            // eslint-disable-next-line no-console
            console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
            this.hasNewVersion.set(true);
            break;
          case 'VERSION_INSTALLATION_FAILED':
            // eslint-disable-next-line no-console
            console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
            break;
        }
      });

      this.swUpdate.unrecoverable.subscribe(() => document.location.reload());
      void this.swUpdate.checkForUpdate();

      const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
      const everyHour$ = interval(60 * 60 * 1000);
      const everyHourOnceAppIsStable$ = concat(appIsStable$, everyHour$);
      everyHourOnceAppIsStable$.subscribe(() => void this.swUpdate.checkForUpdate());
    }
  }

  doAppUpdate(): Promise<unknown> {
    this.hasNewVersion.set(false);
    return this.swUpdate.activateUpdate().then(() => document.location.reload());
  }
}
